import { useEffect } from "react";
import { REGISTER_SYSTEM_AUDIO_TRACK } from "../Actions/Audio/AudioActions";
import { offline, online } from "../Actions/Information/InformationActions";
import { userLogout } from "../Actions/Users/UserActions";
import { clearMediaCentre } from "../Common/Media/Selector/MediaUtilities";
import AutoLogout from "../Utilities/Domain/Users/AutoLogout";
import { getApplicationManager } from "../Utilities/Platform/Applications/ApplicationUtilities";
import { preloadShopfrontAudio } from "../Utilities/Platform/Audio/Audio";
import AudioManager, { AudioManagerEvents } from "../Utilities/Platform/Audio/AudioManager";
import { getLogLevel, LogLevel } from "../Utilities/Platform/Logging";
import AgentManager from "../Workers/Bases/AgentManager";
import type BaseAgent from "../Workers/Bases/BaseAgent";
import DisplayAgent from "../Workers/CustomerDisplay/DisplayAgent";
import HardwareConnectAgent from "../Workers/HardwareConnect/HardwareConnectAgent";
import { SearchAgent } from "../Workers/Search/SearchAgent";
import SocketAgent from "../Workers/Socket/SocketAgent";
import SyncAgent from "../Workers/Sync/SyncAgent";
import TabAgent from "../Workers/Tab/TabAgent";
import UploadAgent from "../Workers/Upload/UploadAgent";
import { UPLOAD_ERROR_EVENT } from "./GlobalConstants";
import { useShopfrontDispatch } from "./hooks";
import { history } from "./RoutingAndStore";

const shopfrontAgents = {} as Record<string, BaseAgent>;

export const useGlobalInitialiseEffect = (): void => {
    const dispatch = useShopfrontDispatch();

    useEffect(() => {
        // Initialise all the agents and related singletons
        shopfrontAgents.sync = AgentManager.getAgent(SyncAgent);
        shopfrontAgents.hardwareConnect = AgentManager.getAgent(HardwareConnectAgent);
        shopfrontAgents.tab = AgentManager.getAgent(TabAgent);
        shopfrontAgents.socket = AgentManager.getAgent(SocketAgent);
        shopfrontAgents.search = AgentManager.getAgent(SearchAgent);
        shopfrontAgents.upload = AgentManager.getAgent(UploadAgent, [
            () => dispatch(offline()),
            () => dispatch(online()),
            () => {
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Logging out due to upload worker");
                }

                dispatch(userLogout());
            },
            (id, type, data) => {
                if(typeof document === "undefined") {
                    return;
                }

                document.dispatchEvent(new CustomEvent(UPLOAD_ERROR_EVENT, {
                    detail: {
                        id,
                        type,
                        data,
                    },
                }));
            },
        ]);

        DisplayAgent.getInstance();
        getApplicationManager().setup();

        const autoLogout = new AutoLogout(60 * 60 * 2);

        AutoLogout.setupListeners();

        const stopListeningToChanges = history.listen(() => {
            clearMediaCentre();
        });

        const audioManager = AudioManager.getInstance();

        const addSystemAudio = () => {
            const track = audioManager.createTrack("SYSTEM_AUDIO");

            void preloadShopfrontAudio(audioManager);

            dispatch({
                type: REGISTER_SYSTEM_AUDIO_TRACK,
                data: {
                    id: track.id,
                },
            });
        };

        let hasListeners = false;

        if(audioManager.isReady()) {
            addSystemAudio();
        } else {
            hasListeners = true;
            audioManager.addListener(AudioManagerEvents.Ready, addSystemAudio);
        }

        return () => {
            getApplicationManager().destroy();
            autoLogout.destroy();
            stopListeningToChanges();

            AutoLogout.removeListeners();

            if(hasListeners) {
                audioManager.removeListener(AudioManagerEvents.Ready, addSystemAudio);
            }
        };
    }, [ dispatch ]);
};
